
import { Component, Vue } from 'vue-property-decorator';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import { ATLAS_FEATURE_LINKS, atlasRedirectLocation, getFeatureVersion, setFeatureVersion } from '@/config/atlasFeatures';
import { SupplierMigrationModel } from '@/models/supplier_migration';
import type { MigrationPreview, JobStatus } from '@/models/supplier_migration';
import {
  StockyMigrationModel,
  StockyRequestError,
  StockySupplierPreview,
  StockySupplierImportResult,
  StockyPOJobStatus,
  StockyVariantAssignmentPreview,
  StockyVariantAssignmentResult,
} from '@/models/stocky_migration';
import type { StockyConflictMode } from '@/models/stocky_migration';
import { stockyReconciliationText } from '@/utils/stockyReconciliation';
import { normalizePOResults, dataWarnings } from '@/utils/stockyPoResults';
import {
  isFailed,
  isQueued,
  isRunning,
  isTerminal,
  queueWaitColor,
  queueWaitCopy,
} from '@/utils/stockyJobStatus';
import type { QueueWaitCopy } from '@/utils/stockyJobStatus';

const STATUS_COLORS: Record<string, string> = {
  draft: '#9E9E9E',
  confirmed: '#1976D2',
  partial_received: '#FF9800',
  received: '#4CAF50',
  completed: '#4CAF50',
  canceled: '#F44336',
};

@Component({
  components: {
    CopyLinkButton,
    TryNewAtlasBanner,
  },
})
export default class StockyDataMigration extends Vue {
  private step = 0;
  private loading = false;
  private initialLoading = true;
  private error: string | null = null;

  // Step 0: Connection
  private stockyApiKey = '';
  private isConnected = false;
  private hasSavedKey = false;
  private savedKeyMask = '';
  private isChangingKey = false;
  private previousStep = 0;

  // Step 1: Supplier preview
  private supplierPreview: StockySupplierPreview | null = null;
  private supplierConflictResolutions: Record<number, string> = {};
  private supplierImportResult: StockySupplierImportResult | null = null;
  private isSuppliersImported = false;

  // Step 2: Shopify vendors → suppliers
  private vendorPreview: MigrationPreview | null = null;
  /** Global resolution for all conflicts — the backend accepts one strategy per job. */
  private vendorConflictResolution: 'skip' | 'update' = 'skip';
  private vendorJobStatus: JobStatus | null = null;
  private vendorPollingInterval: number | null = null;
  /**
   * Guards against overlapping status requests: a slow `processing` response
   * arriving after `completed` would overwrite the terminal status and leave
   * the step stuck in the active state with every button hidden.
   */
  private isVendorPollInFlight = false;
  private isVendorsImported = false;
  private vendorTableHeaders = [
    { text: 'Vendor', value: 'name' },
    { text: 'Product Variants', value: 'productCount' },
    { text: 'Status', value: 'status', sortable: false },
  ];

  // Step 3: PO status selection
  private selectedPOStatuses: string[] = [
    'draft-unarchived',
    'confirmed-unarchived',
    'draft-archived',
    'confirmed-archived',
  ];
  private poStatusOptions = [
    { value: 'confirmed-unarchived', label: 'Active', description: 'Confirmed, partially received, and received POs', icon: 'mdi-check-circle', color: 'success' },
    { value: 'draft-unarchived', label: 'Draft', description: 'POs still in draft status', icon: 'mdi-pencil-circle', color: 'grey' },
    { value: 'confirmed-archived', label: 'Archived (Confirmed)', description: 'Completed and archived confirmed POs', icon: 'mdi-archive', color: 'teal' },
    { value: 'draft-archived', label: 'Archived (Draft)', description: 'Archived draft POs', icon: 'mdi-archive-outline', color: 'blue-grey' },
    { value: 'other', label: 'Other / remaining statuses', description: 'Any PO whose status is not listed above', icon: 'mdi-dots-horizontal-circle', color: 'blue-grey' },
  ];

  /**
   * Which side wins for a PO the merchant has received against or edited here
   * since it was last imported. Defaults to protecting local work.
   *
   * Kept in step with StockyMigrationWizard.vue, which runs the same import from
   * the Supplier List dialog. Until this was added, this page always sent the
   * backend default, so a merchant reaching the migration from the menu had no
   * way to refresh a PO carrying receipts — it reported "not refreshed from
   * Stocky because it has receive records" with nothing to do about it.
   */
  private poConflictMode: StockyConflictMode = 'skip';
  private poConflictModeOptions = [
    {
      value: 'skip',
      label: 'Keep my Assisty changes',
      description: 'Leave those purchase orders exactly as they are. Nothing is imported over them.',
      icon: 'mdi-shield-check',
      color: 'success',
    },
    {
      value: 'overwrite',
      label: 'Replace them with Stocky data',
      description: 'Rebuild those purchase orders from Stocky, discarding receipts, returns and edits made in Assisty.',
      icon: 'mdi-alert-circle',
      color: 'warning',
    },
  ];

  // Step 4: PO import
  private jobId: string | null = null;
  private jobStatus: StockyPOJobStatus | null = null;
  private pollingInterval: number | null = null;

  /**
   * Guards against overlapping PO status requests, the same way
   * `isVendorPollInFlight` does for the vendor job.
   *
   * `setInterval` does not wait for its async callback, so a slow request can
   * still be in flight when a later one fails, records the terminal status and
   * stops the interval. The slow one then resolves and writes `processing` back
   * over it — leaving the page active with no poller left to change it, which is
   * exactly the dead end the terminal status exists to prevent.
   */
  private isImportPollInFlight = false;

  /**
   * True when this screen is following an import it did not start — the store's
   * run lock was already held and the backend told us which job holds it.
   * Worth saying out loud: the progress on screen is someone else's run, so the
   * status filters and conflict mode chosen here are not the ones being applied.
   */
  private resumedExistingImport = false;

  /**
   * True when polling died on us rather than the import failing. The job is
   * marked `error` so the page stops treating it as active and gives the
   * controls back, but "Import Failed" would be a guess — the import is on the
   * backend and very likely still running.
   */
  private pollingLostContact = false;
  /** When the import was submitted, used to age the queue-wait message. */
  private jobStartedAt: number | null = null;
  /**
   * Advanced by the poll tick rather than a second timer — the queue copy is
   * deliberately coarse (minutes), so a 2s cadence is more than enough.
   */
  private queueElapsedMs = 0;

  // Step 5: Variant assignments
  private variantCsvFile: File | null = null;
  private variantPreview: StockyVariantAssignmentPreview | null = null;
  private variantImportResult: StockyVariantAssignmentResult | null = null;
  private isDragover = false;
  private overrideExisting = false;
  private variantStepSkipped = false;
  private skippedSteps: number[] = [];

  private stepLabels = ['Connect', 'Suppliers', 'Vendors', 'Select POs', 'Import', 'Products'];

  private migrationFeatures = [
    'Suppliers and contact information',
    'Shopify vendors as suppliers (optional)',
    'Tax types and rates',
    'All purchase orders (draft, confirmed, received, archived)',
    'PO line items matched to your Shopify products',
    'Receive history',
    'Supplier-product relationships',
  ];

  private conflictResolutionOptions = [
    { text: 'Skip', value: 'skip' },
    { text: 'Update', value: 'update' },
    { text: 'Keep Both', value: 'keep_both' },
  ];

  private supplierTableHeaders = [
    { text: 'Name', value: 'name' },
    { text: 'Email', value: 'contactEmail' },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Action', value: 'action', sortable: false },
  ];


  private variantSupplierHeaders = [
    { text: 'Supplier Name', value: 'name' },
    { text: 'Variants', value: 'variantCount' },
    { text: 'Status', value: 'matched', sortable: false },
  ];

  async mounted() {
    try {
      const status = await StockyMigrationModel.getConnection();
      if (status.hasKey) {
        this.hasSavedKey = true;
        this.savedKeyMask = status.maskedKey || '';
        this.isConnected = true;

        // If query param requests direct jump to products step
        if (this.$route.query.step === 'products') {
          this.step = 5;
          return;
        }

        // Skip Connect step — go directly to Suppliers
        this.step = 1;
        try {
          await this.fetchSupplierPreview();
        } catch {
          // Saved key is invalid — fall back to Connect step
          this.step = 0;
          this.isConnected = false;
          this.hasSavedKey = false;
          this.error = 'Your saved Stocky API key is no longer valid. Please enter a new key.';
        }
      }
    } catch {
      // Key missing or check failed — user will enter key manually
    } finally {
      this.initialLoading = false;
    }
  }

  get currentTitle(): string {
    switch (this.step) {
      case 0: return 'Migrate from Stocky';
      case 1: return 'Migrate from Stocky — Suppliers';
      case 2: return 'Migrate from Stocky — Shopify Vendors';
      case 3: return 'Migrate from Stocky — Select POs';
      case 4: return 'Migrate from Stocky — Import';
      case 5: return 'Migrate from Stocky — Product Assignments';
      default: return 'Migrate from Stocky';
    }
  }

  /** Vendor import job is pending or processing — the step must not be left. */
  get isVendorImportActive(): boolean {
    return this.vendorJobStatus !== null
      && (this.vendorJobStatus.status === 'pending' || this.vendorJobStatus.status === 'processing');
  }

  /**
   * `not_found` counts as failed: the job record is gone (Redis eviction), so
   * there is nothing to show and nothing to carry forward — same reasoning as
   * `isFailed()` in stockyJobStatus.ts. The shared helpers are not reused here
   * because the two job models have different status unions (this one has
   * `pending`, the Stocky PO one has `created`).
   */
  get isVendorImportFailed(): boolean {
    return this.vendorJobStatus !== null
      && (this.vendorJobStatus.status === 'error' || this.vendorJobStatus.status === 'not_found');
  }

  get vendorFailureHeadline(): string {
    return this.vendorJobStatus?.status === 'not_found' ? 'Import no longer tracked' : 'Vendor Import Failed';
  }

  get vendorFailureMessage(): string {
    if (this.vendorJobStatus?.status === 'not_found') {
      return 'We lost track of this import — its progress record expired or was cleared. '
        + 'Suppliers it already created are safe; retry to start a new import.';
    }
    return this.vendorJobStatus?.error || this.vendorJobStatus?.message || 'An unknown error occurred.';
  }

  get vendorConflictCount(): number {
    return this.vendorPreview
      ? this.vendorPreview.vendors.filter(v => v.conflict).length
      : 0;
  }

  get vendorNewCount(): number {
    return this.vendorPreview
      ? this.vendorPreview.vendors.length - this.vendorConflictCount
      : 0;
  }

  get vendorImportSummary(): JobStatus['summary'] | null {
    if (!this.vendorJobStatus) return null;
    return this.vendorJobStatus.results?.summary || this.vendorJobStatus.summary || null;
  }

  /**
   * Queued, not yet picked up by a worker. Rendered as its own state: there is
   * no progress to show, and a 0% ring here reads as a stuck import.
   */
  get isImportQueued(): boolean {
    return this.jobStatus !== null && isQueued(this.jobStatus.status);
  }

  get isImportRunning(): boolean {
    return this.jobStatus !== null && isRunning(this.jobStatus.status);
  }

  /**
   * Queued or running. What the Step 3 navigation guards care about: a queued
   * import is every bit as in-flight as a running one, and `handleAction`
   * advances to Step 4 unconditionally — so leaving Next/Skip live during the
   * queue wait lets a merchant walk past an import that has not started.
   */
  get isImportActive(): boolean {
    return this.isImportQueued || this.isImportRunning;
  }

  get isImportDone(): boolean {
    return this.jobStatus !== null
      && (this.jobStatus.status === 'completed' || this.jobStatus.status === 'error');
  }

  /** Terminal with nothing to carry forward — Step 3 must not advance. */
  get isImportFailed(): boolean {
    return this.jobStatus !== null && isFailed(this.jobStatus.status);
  }

  get importFailureHeadline(): string {
    // Losing the status endpoint says nothing about the import, which runs on
    // the backend regardless of whether this tab can see it.
    if (this.pollingLostContact) {
      return 'Import progress unavailable';
    }
    // A lost job record is not the same as a failed import — the import may
    // well have finished. Saying "Import Failed" there would be a guess.
    return this.jobStatus?.status === 'not_found' ? 'Import no longer tracked' : 'Import Failed';
  }

  get importFailureMessage(): string {
    if (this.jobStatus?.status === 'not_found') {
      return 'We lost track of this import — its progress record expired or was cleared. '
        + 'Any purchase orders it already imported are safe; re-running is safe too, because '
        + 'existing POs are updated rather than duplicated.';
    }
    return this.jobStatus?.error || 'An unknown error occurred.';
  }

  get queueWait(): QueueWaitCopy {
    return queueWaitCopy(this.queueElapsedMs);
  }

  get queueWaitColor(): string {
    return queueWaitColor(this.queueWait.severity);
  }

  get reconciliationSummary(): string {
    const results = this.jobStatus?.results;
    if (!results) {
      return '';
    }
    // `results` is Partial — jobs predating the newer counters omit fields.
    // Normalizing fills them in, so the reconciliation text always gets real
    // numbers instead of `undefined`.
    return stockyReconciliationText(normalizePOResults(results));
  }

  /**
   * Signals that the import may not reflect all of Stocky, plus anything an
   * overwrite destroyed. Not failures, so they do not belong in the errors
   * panel — but a merchant must not treat the run as complete without them.
   *
   * Load-bearing now that this page can run in `overwrite`: the losses it
   * causes are irreversible, and reporting them is the only record the merchant
   * gets. Also carries the "-stocky" rename explanation, which was previously
   * silent here.
   */
  get poDataWarnings(): string[] {
    const results = this.jobStatus?.results;
    if (!results) {
      return [];
    }
    return dataWarnings(normalizePOResults(results));
  }

  get hasUnreconciled(): boolean {
    return (this.jobStatus?.results?.unreconciled ?? 0) > 0;
  }

  get otherStatusLabel(): string {
    return this.poStatusOptions.find(o => o.value === 'other')?.label ?? '';
  }

  get showActionButton(): boolean {
    if (this.step === 2 && this.isVendorImportActive) return false;
    if (this.step === 4 && this.isImportActive) return false;
    if (this.step === 4 && this.isImportFailed) return false;
    if (this.step === 4 && this.jobStatus === null) return false;
    if (this.step === 5 && this.variantImportResult) return false;
    if (this.step === 5 && this.variantStepSkipped) return false;
    if (this.step === 5 && !this.variantCsvFile && !this.variantPreview) return false;
    return true;
  }

  get showBackButton(): boolean {
    if (this.step === 0) return false;
    if (this.step === 2 && this.isVendorImportActive) return false;
    if (this.step < 4) return !this.isImportDone;
    if (this.step === 4) return this.jobStatus === null || this.isImportFailed;
    return !this.variantImportResult && !this.variantStepSkipped;
  }

  get canSkipCurrentStep(): boolean {
    if (this.step === 0) return false;
    if (this.step === 2 && (this.isVendorImportActive || this.isVendorsImported)) return false;
    if (this.step === 4 && this.isImportActive) return false;
    if (this.step === 5 && (this.variantImportResult || this.variantStepSkipped)) return false;
    return true;
  }

  get actionButtonText(): string {
    switch (this.step) {
      case 0:
        return this.isConnected ? 'Next' : 'Test Connection';
      case 1:
        return this.isSuppliersImported ? 'Next' : 'Import Suppliers';
      case 2:
        if (this.isVendorsImported) return 'Next';
        if (this.isVendorImportFailed) return 'Retry Import';
        if (!this.vendorPreview) return 'Retry Preview';
        if (this.vendorPreview.vendors.length > 0) return 'Import Vendors';
        return 'Next';
      case 3:
        return 'Import Purchase Orders';
      case 4:
        return 'Next: Link Products';
      case 5:
        if (this.variantPreview) return 'Import Assignments';
        return 'Preview';
      default:
        return 'Next';
    }
  }

  get isActionDisabled(): boolean {
    if (this.loading) return true;
    if (this.step === 0 && !this.isConnected && !this.stockyApiKey.trim()) return true;
    if (this.step === 3 && this.selectedPOStatuses.length === 0) return true;
    if (this.step === 5 && !this.variantPreview && !this.variantCsvFile) return true;
    return false;
  }

  stepClass(idx: number): string {
    if (this.skippedSteps.includes(idx)) return 'step-indicator--skipped';
    if (idx < this.step) return 'step-indicator--done';
    if (idx === this.step) return 'step-indicator--active';
    return 'step-indicator--pending';
  }

  isStepSkipped(idx: number): boolean {
    return this.skippedSteps.includes(idx);
  }

  goBack() {
    // Defence in depth: the Back button is already hidden while either import
    // job is active (showBackButton), so these are not reachable today. They
    // guard against a future template change quietly reopening the path.
    if (this.step === 4 && this.isImportActive) {
      return;
    }
    if (this.step === 2 && this.isVendorImportActive) {
      return;
    }
    if (this.step > 0) {
      this.step -= 1;
    }
  }

  togglePOStatus(value: string) {
    const idx = this.selectedPOStatuses.indexOf(value);
    if (idx >= 0) {
      this.selectedPOStatuses.splice(idx, 1);
    } else {
      this.selectedPOStatuses.push(value);
    }
  }

  startKeyChange() {
    this.previousStep = this.step;
    this.isChangingKey = true;
    this.isConnected = false;
    this.step = 0;
    this.stockyApiKey = '';
    this.error = null;
  }

  cancelKeyChange() {
    this.isChangingKey = false;
    this.isConnected = true;
    this.step = this.previousStep;
    this.stockyApiKey = '';
    this.error = null;
  }

  resetConnection() {
    this.step = 0;
    this.isConnected = false;
    this.hasSavedKey = false;
    this.savedKeyMask = '';
    this.stockyApiKey = '';
    this.isChangingKey = false;
    this.previousStep = 0;
    this.error = null;
    this.supplierPreview = null;
    this.supplierImportResult = null;
    this.isSuppliersImported = false;
  }

  async handleAction() {
    this.error = null;

    if (this.step === 0) {
      if (this.isConnected) {
        this.loading = true;
        this.step = 1;
        await this.fetchSupplierPreview();
      } else {
        await this.testConnection();
      }
      return;
    }

    if (this.step === 1) {
      if (this.isSuppliersImported) {
        this.step = 2;
        await this.ensureVendorPreview();
      } else {
        await this.importSuppliers();
      }
      return;
    }

    if (this.step === 2) {
      if (this.isVendorsImported) {
        this.step = 3;
      } else if (!this.vendorPreview) {
        // Preview failed to load — retry it rather than silently advancing past
        // the vendor import. Skip remains the deliberate way to bypass it.
        await this.ensureVendorPreview();
      } else if (this.vendorPreview.vendors.length === 0) {
        this.step = 3;
      } else {
        await this.startVendorImport();
      }
      return;
    }

    if (this.step === 3) {
      this.step = 4;
      await this.startPOImport();
      return;
    }

    if (this.step === 4) {
      // PO import done — move to variant assignments
      this.step = 5;
      return;
    }

    if (this.step === 5) {
      if (this.variantPreview) {
        await this.importVariantAssignments();
      } else {
        await this.previewVariantAssignments();
      }
    }
  }

  async testConnection() {
    this.loading = true;
    this.error = null;
    try {
      await StockyMigrationModel.connect(this.stockyApiKey);
      this.isConnected = true;
      this.hasSavedKey = true;
      this.savedKeyMask = this.stockyApiKey.slice(0, 6) + '...' + this.stockyApiKey.slice(-4);
    } catch (err: any) {
      this.error = err.response?.data?.error
        || err.message
        || 'Failed to connect to Stocky. Please check your API key.';
    } finally {
      this.loading = false;
    }
  }

  async fetchSupplierPreview() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierPreview = await StockyMigrationModel.previewSuppliers();
      if (this.supplierPreview) {
        for (const supplier of this.supplierPreview.suppliers) {
          if (supplier.conflict) {
            this.$set(this.supplierConflictResolutions, supplier.stockyId, 'skip');
          }
        }
      }
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to load supplier preview.';
    } finally {
      this.loading = false;
    }
  }

  /** Fetch the vendor preview when landing on the Vendors step, once. */
  async ensureVendorPreview() {
    if (this.vendorPreview || this.vendorJobStatus) {
      return;
    }
    this.loading = true;
    this.error = null;
    try {
      this.vendorPreview = await SupplierMigrationModel.getMigrationPreview('vendor');
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to load Shopify vendors.';
    } finally {
      this.loading = false;
    }
  }

  async startVendorImport() {
    this.error = null;
    try {
      const job = await SupplierMigrationModel.startMigration({
        source: 'vendor',
        conflictResolution: this.vendorConflictResolution,
      });
      this.vendorJobStatus = { status: 'pending', message: 'Import job started...' };
      this.startVendorPolling(job.jobId);
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to start vendor import.';
    }
  }

  startVendorPolling(jobId: string) {
    this.isVendorPollInFlight = false;
    this.vendorPollingInterval = window.setInterval(async () => {
      if (this.isVendorPollInFlight) {
        return;
      }
      this.isVendorPollInFlight = true;
      try {
        const status = await SupplierMigrationModel.getMigrationStatus(jobId);
        this.vendorJobStatus = status;

        // `not_found` is terminal too — the job record is gone, so polling it
        // forever would leave the step blank with no way out.
        if (status.status === 'completed' || status.status === 'error' || status.status === 'not_found') {
          this.stopVendorPolling();
          if (status.status === 'completed') {
            this.isVendorsImported = true;
          }
        }
      } catch (err) {
        this.stopVendorPolling();
        this.error = 'Failed to get vendor import status.';
      } finally {
        this.isVendorPollInFlight = false;
      }
    }, 2000);
  }

  stopVendorPolling() {
    if (this.vendorPollingInterval) {
      clearInterval(this.vendorPollingInterval);
      this.vendorPollingInterval = null;
    }
  }

  async importSuppliers() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierImportResult = await StockyMigrationModel.importSuppliers(
        this.supplierConflictResolutions
      );
      this.isSuppliersImported = true;
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to import suppliers.';
    } finally {
      this.loading = false;
    }
  }

  async startPOImport() {
    this.error = null;
    this.resumedExistingImport = false;
    try {
      const job = await StockyMigrationModel.importPurchaseOrders(
        this.selectedPOStatuses,
        this.poConflictMode,
      );
      await this.attachToJob(job.jobId, { resumed: false });
    } catch (err: any) {
      // 409 means another import already holds this store's run lock — usually
      // one started in another tab, or by a colleague. Nothing is broken and
      // nothing was duplicated, so when the backend names that run, follow it
      // instead of sending the merchant away to try again later.
      const conflict = err instanceof StockyRequestError && err.statusCode === 409;
      if (conflict && err.jobId) {
        this.resumedExistingImport = true;
        await this.attachToJob(err.jobId, { resumed: true });
        return;
      }
      // No job id: the lock is held by a run that died before it created one.
      // There is nothing to watch, so this stays the message it always was.
      //
      // Read from `err.message`, not `err.response`: importPurchaseOrders throws
      // a StockyRequestError, which has no `response` — the old
      // `err.response?.data?.error` here could never match and silently fell
      // through to the message every time.
      this.error = err.message || 'Failed to start purchase order import.';
    }
  }

  startPolling(jobId: string) {
    // Reset in case a previous run left it set, as startVendorPolling does.
    this.isImportPollInFlight = false;
    this.pollingInterval = window.setInterval(async () => {
      if (this.isImportPollInFlight) {
        return;
      }
      this.isImportPollInFlight = true;
      try {
        const status = await StockyMigrationModel.getImportStatus(jobId);
        this.jobStatus = status;

        if (this.jobStartedAt !== null) {
          this.queueElapsedMs = Date.now() - this.jobStartedAt;
        }

        if (isTerminal(status.status)) {
          this.stopPolling();
        }

        // The job record is gone — Redis eviction, or a stale id from a
        // previous session. Polling it forever would leave the wizard sitting
        // on a screen that can never change, so say so instead.
        if (status.status === 'not_found') {
          this.error = 'This import is no longer being tracked. Re-run the import to start a new one.';
        }
      } catch (err) {
        // Polling is the only thing that can move the job out of an active
        // state, so stopping it without recording a terminal one strands the
        // page: `isImportActive` stays true, which hides the action and back
        // buttons with nothing left running to change them. Fail it explicitly.
        this.pollingLostContact = true;
        this.jobStatus = {
          status: 'error',
          error: 'We could not read this import\'s progress. The import itself is most likely '
            + 'still running — reopen this page shortly to check on it. Re-running is safe '
            + 'either way, because existing purchase orders are updated rather than duplicated.',
        };
        this.stopPolling();
      } finally {
        this.isImportPollInFlight = false;
      }
    }, 2000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  getStatusColor(status: string): string {
    return STATUS_COLORS[status] || '#9E9E9E';
  }

  formatStatus(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Draft',
      confirmed: 'Confirmed',
      partial_received: 'Partial',
      received: 'Received',
      completed: 'Completed',
      canceled: 'Canceled',
    };
    return labels[status] || status;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  handleFileDrop(event: DragEvent) {
    this.isDragover = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        this.variantCsvFile = file;
      } else {
        this.error = 'Only .csv files are allowed.';
      }
    }
  }

  handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        this.variantCsvFile = file;
      } else {
        this.error = 'Only .csv files are allowed.';
        input.value = '';
      }
    }
  }

  triggerFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement)?.click();
  }

  clearCsvFile() {
    this.variantCsvFile = null;
    const input = this.$refs.csvFileInput as HTMLInputElement;
    if (input) input.value = '';
  }

  resetVariantStep() {
    this.variantPreview = null;
    this.variantCsvFile = null;
    this.overrideExisting = false;
    this.error = null;
    const input = this.$refs.csvFileInput as HTMLInputElement;
    if (input) input.value = '';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async previewVariantAssignments() {
    if (!this.variantCsvFile) return;
    this.loading = true;
    this.error = null;
    try {
      this.variantPreview = await StockyMigrationModel.previewVariantAssignments(this.variantCsvFile);
    } catch (err: any) {
      this.error = err.message || 'Failed to preview variant assignments.';
    } finally {
      this.loading = false;
    }
  }

  async importVariantAssignments() {
    if (!this.variantCsvFile) return;
    this.loading = true;
    this.error = null;
    try {
      this.variantImportResult = await StockyMigrationModel.importVariantAssignments(
        this.variantCsvFile,
        { overrideExisting: this.overrideExisting }
      );
    } catch (err: any) {
      this.error = err.message || 'Failed to import variant assignments.';
    } finally {
      this.loading = false;
    }
  }

  skipStep() {
    if (this.step === 5) {
      this.variantStepSkipped = true;
      return;
    }
    if (!this.skippedSteps.includes(this.step)) {
      this.skippedSteps.push(this.step);
    }
    this.step += 1;
    // Skipping the Suppliers step still lands on Vendors — load its preview.
    if (this.step === 2) {
      this.ensureVendorPreview();
    }
  }

  navigateTo(path: string) {
    this.$router.push(path).catch(() => {});
  }

  private get showTryNewStockyBanner(): boolean {
    return getFeatureVersion('stockyMigration') === 'classic';
  }

  private switchStockyToAtlas(): void {
    setFeatureVersion('stockyMigration', 'atlas');
    // Carry the current query across. A merchant who arrived from Suppliers ->
    // "Link Products from Stocky CSV" is on ?step=products; pushing the bare
    // path drops it and restarts them at step 0 of a six-step wizard. The
    // router guard already merges the query via this helper on the redirect
    // path — this is the same journey by a different door, so it uses it too.
    const location = atlasRedirectLocation(
      ATLAS_FEATURE_LINKS.stockyMigration['/stocky-data-migration'],
      this.$route.query as Record<string, string>,
    );
    this.$router.push(location);
  }

  beforeDestroy() {
    this.stopPolling();
    this.stopVendorPolling();
  }

  /**
   * Point this screen at a running import and follow it to completion.
   *
   * `resumed` marks an import that was already under way. Its real start time is
   * unknown — the backend records no createdAt — so `jobStartedAt` stays null,
   * which keeps the queue-wait copy silent rather than having it report a wait
   * measured from the moment we attached.
   */
  private async attachToJob(jobId: string, { resumed }: { resumed: boolean }) {
    this.jobId = jobId;
    this.pollingLostContact = false;
    // Mirror what the backend has actually recorded — the job is enqueued and
    // waiting for a worker. Seeding a status the backend never sends is what
    // used to make the first poll flip the page into an unrendered state.
    this.jobStatus = { status: 'created' };
    this.jobStartedAt = resumed ? null : Date.now();
    this.queueElapsedMs = 0;

    if (resumed) {
      // The run is already in progress, so read its real state now rather than
      // showing the queued panel until the first poll comes round.
      try {
        this.jobStatus = await StockyMigrationModel.getImportStatus(jobId);
      } catch (statusError) {
        // Keep the seeded status; the poll below will correct it.
      }
    }

    this.startPolling(jobId);
  }
}
